import type { ErrorCodes, ClientErrorCodes } from '../errorCodes';
import type { QuizPlayerScore } from './common';

export interface GameStatePayload<
  GameState extends Exclude<GameStateEnums, GameStateEnums.ERROR>,
  GameStateData,
> {
  errorCode: null;
  gameState: GameState;
  gameStateData: GameStateData;
}

export interface ErrorStatePayload<
  ErrorCode extends ErrorCodes | ClientErrorCodes,
  GameStateData,
> {
  errorCode: ErrorCode;
  gameState: GameStateEnums.ERROR;
  gameStateData: GameStateData;
}
export enum GameStateEnums {
  LOBBY = 'lobby',
  COUNTDOWN = 'countdown',
  GET_READY = 'get_ready',
  QUESTION = 'question',
  RESULT = 'result',
  ERROR = 'error',
  LEADERBOARD = 'leaderboard',
  FINAL_LEADERBOARD = 'final_leaderboard',
}

export type InitialGameStates =
  | GameStateEnums.FINAL_LEADERBOARD
  | GameStateEnums.LEADERBOARD
  | GameStateEnums.RESULT
  | GameStateEnums.LOBBY;

export type LobbyGameState = GameStatePayload<
  GameStateEnums.LOBBY,
  { questionPublicKey: string }
>;

export type QuizCreatedGameState = GameStatePayload<
  InitialGameStates,
  { questionPublicKey: string; score?: QuizPlayerScore['score'] }
>;
export type QuizEndedGameState = GameStatePayload<
  GameStateEnums.RESULT,
  { questionPublicKey: string; score?: QuizPlayerScore['score'] }
>;
export type GetReadyGameState = GameStatePayload<
  GameStateEnums.GET_READY,
  { questionPublicKey: string }
>;
export type CountdownGameState = GameStatePayload<
  GameStateEnums.COUNTDOWN,
  { questionPublicKey: string; endAt: number; startAt: number }
>;

// voter-specific view of the state (ew)
export type ResultScoreGameState = GameStatePayload<
  GameStateEnums.RESULT,
  { score: QuizPlayerScore['score'] }
>;

// voter-specific view of the state (ew)
export type ResultGameState = GameStatePayload<
  GameStateEnums.RESULT,
  { questionPublicKey: string; score: QuizPlayerScore['score'] }
>;

export type QuestionGameState = GameStatePayload<
  GameStateEnums.QUESTION,
  { elapsedTime: number }
>;

type LeaderboardGameState = GameStatePayload<
  GameStateEnums.LEADERBOARD,
  { score: QuizPlayerScore['score'] }
>;

type FinalLeaderboardGameState = GameStatePayload<
  GameStateEnums.FINAL_LEADERBOARD,
  { score: QuizPlayerScore['score'] }
>;

type ErrorGameState = ErrorStatePayload<
  ErrorCodes | ClientErrorCodes,
  Record<string, unknown>
>;

export type GameStates =
  | LobbyGameState
  | QuizCreatedGameState
  | QuizEndedGameState
  | GetReadyGameState
  | CountdownGameState
  | QuestionGameState
  | ResultScoreGameState
  | ResultGameState
  | LeaderboardGameState
  | FinalLeaderboardGameState
  | ErrorGameState;
